import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { useState, useMemo, useCallback } from 'react';
import { useMount } from 'ahooks';
import { getLabel, useOptions } from 'src/pages/conversationAnalysis/hooks';
import { Button, FormBuilder, Space, Popconfirm } from 'udesk-ui';
import ImageViewer from 'src/components/common/ImageViewer';
import Page from 'Component/common/udesk-qa-page-layout';
import CommonTablePage from 'Component/common/udesk-qa-table-page';
import CheckReportButton from './components/CheckReportButton';
import type { FormProps } from 'udesk-ui/lib/form-builder/types/form';
import { getMainProducts } from 'src/api/mainProducts';
import { deleteMainProductsById } from 'src/api/mainProducts/{id}';
import type { TableComponentProps } from 'Component/common/udesk-qa-table-page';
import { PointTemplateQueryParam, MainProductFoundResponse } from 'src/api/types';
import { getCurrentRouteName } from 'Udesk/system/subApp';
import { CREATE_ID } from '../detail/const';

const Template = React.memo((props: any) => {
    const { history } = props;

    const locales = UdeskLocales['current'];

    const [searchData, setSearchData] = useState<PointTemplateQueryParam>();
    const [loading, setLoading] = useState(false);
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const [dataSource, setDataSource] = useState<MainProductFoundResponse[]>([]);

    const sendRequest = (searchData, pageNum, pageSize) => {
        setLoading(true);
        getMainProducts({
            params: { ...searchData, pageNum, pageSize },
        }).then(
            (resp) => {
                setLoading(false);
                const { data = [], paging } = resp;
                setPageNum(paging?.pageNum ?? 1);
                setPageSize(paging?.pageSize ?? 20);
                setTotal(paging?.total ?? 0);
                setDataSource(data);
            },
            (reason) => {
                setLoading(false);
                Udesk.ui.notify.error(reason.errorMsg);
            }
        );
    };

    useMount(() => {
        sendRequest(searchData, pageNum, pageSize);
    });

    const search = useCallback(
        (values: PointTemplateQueryParam) => {
            setSearchData(values);
            sendRequest(values, 1, pageSize);
        },
        [pageSize]
    );

    const [form] = FormBuilder.useForm();

    const config: FormProps = {
        fields: [
            {
                name: 'name',
                label: '',
                type: 'Input',
                props: {
                    placeholder:
                        /* 请输入关键字 */ UdeskLocales['current'].pages.conversationAnalysis
                            .productManage.list.index.pleaseEnterAKeyword,
                    allowClear: true,
                },
            },
        ],
        layout: 'inline',
        footerRender: () => (
            <Button onClick={() => form?.submit?.()} type="primary">
                {locales.labels.query}
            </Button>
        ),
        onFinish: search,
    };

    const { ECommerceTypeOptions } = useOptions();

    const columns = useMemo<any[]>(() => {
        const onEdit = (id) => {
            id &&
                Udesk.ui.routing.transitionTo({
                    history,
                    routeName: getCurrentRouteName('productManageDetail'),
                    pathParams: { id },
                });
        };

        const onDel = (id) => {
            id &&
                deleteMainProductsById({ segments: { id } }).then(() => {
                    setPageNum(1);

                    sendRequest(searchData, 1, pageSize);
                });
        };

        return [
            {
                key: 'image',
                dataIndex: 'image',
                title: /* 产品图片 */ UdeskLocales['current'].pages.conversationAnalysis
                    .productManage.list.index.productPicture,
                width: '100px',
                render: (v, item) => {
                    return v ? (
                        <ImageViewer>
                            <img width={80} src={v} alt="" />
                        </ImageViewer>
                    ) : (
                        '-'
                    );
                },
            },
            {
                key: 'name',
                dataIndex: 'name',
                title: /* 产品名称 */ UdeskLocales['current'].pages.conversationAnalysis
                    .productManage.list.index.productName,
                width: '10%',
            },
            {
                key: 'description',
                dataIndex: 'description',
                title: /* 产品描述 */ UdeskLocales['current'].pages.conversationAnalysis
                    .productManage.list.index.productDescription,
                width: '15%',
                render: (v) => {
                    return v ?? '-';
                },
            },
            {
                key: 'createTime',
                dataIndex: 'createTime',
                title: /* 创建时间 */ UdeskLocales['current'].pages.conversationAnalysis
                    .productManage.list.index.creationTime,
                width: '10%',
            },
            {
                key: 'updateTime',
                dataIndex: 'updateTime',
                title: /* 更新时间 */ UdeskLocales['current'].pages.conversationAnalysis
                    .productManage.list.index.updateTime,
                width: '10%',
            },
            {
                key: 'channelSourceList',
                dataIndex: 'channelSourceList',
                title: /* 电商平台 */ UdeskLocales['current'].pages.conversationAnalysis
                    .productManage.list.index.ecommercePlatform,
                width: '10%',
                render: (channelSourceList) => {
                    return (
                        channelSourceList
                            ?.map((c) => getLabel(ECommerceTypeOptions, c))
                            .join(',') || '-'
                    );
                },
            },
            {
                key: 'subProductSize',
                dataIndex: 'subProductSize',
                title: /* 子产品数量 */ UdeskLocales['current'].pages.conversationAnalysis
                    .productManage.list.index.numberOfSubproducts,
                width: '10%',
            },
            {
                title: /* 操作 */ UdeskLocales['current'].pages.intelligentAnalysis.ruleTemplate
                    .index.operation,
                dataIndex: 'actions',
                key: 'actions',
                align: 'center',
                render: (name, item, index) => {
                    return (
                        <Space size="small">
                            <Button size="small" type="link" onClick={() => onEdit(item.id)}>
                                {/* 编辑 */}
                                {
                                    UdeskLocales['current'].pages.conversationAnalysis.productManage
                                        .list.index.edit
                                }
                            </Button>

                            <CheckReportButton productId={item.id} />

                            <Popconfirm
                                title={
                                    /* 确定删除吗？ */ UdeskLocales['current'].pages
                                        .conversationAnalysis.productManage.list.index
                                        .areYouSureToDeleteIt
                                }
                                onConfirm={() => onDel(item.id)}
                            >
                                <Button size="small" type="link" danger>
                                    {/* 删除 */}
                                    {
                                        UdeskLocales['current'].pages.conversationAnalysis
                                            .productManage.list.index.delete
                                    }
                                </Button>
                            </Popconfirm>
                        </Space>
                    );
                },
            },
        ];
    }, [ECommerceTypeOptions, history, pageSize, searchData]);

    const tableProps = useMemo<TableComponentProps>(() => {
        return {
            columns,
            dataSource,
            cacheKey: false,
        };
    }, [columns, dataSource]);

    const paginationProps = useMemo(() => {
        return {
            current: pageNum,
            pageSize,
            total,
            onChange: (page, pageSize) => {
                setPageNum(page);
                setPageSize(pageSize);
                sendRequest(searchData, page, pageSize);
            },
        };
    }, [pageNum, pageSize, total, searchData]);

    const onCreate = () => {
        Udesk.ui.routing.transitionTo({
            history,
            routeName: getCurrentRouteName('productManageDetail'),
            pathParams: { id: CREATE_ID },
        });
    };

    return (
        <Page
            pageBodyClassName="xxx-page-index"
            title={
                /* 产品管理 */ UdeskLocales['current'].pages.conversationAnalysis.productManage.list
                    .index.productManagement
            }
            padding={true}
        >
            <CommonTablePage
                loading={loading}
                tableHeaderLeftRender={<FormBuilder {...config} form={form} />}
                mainButton={
                    <Button type="primary" onClick={() => onCreate()}>
                        {/* 新建产品 */}
                        {
                            UdeskLocales['current'].pages.conversationAnalysis.productManage.list
                                .index.newProduct
                        }
                    </Button>
                }
            >
                <CommonTablePage.Table {...tableProps}></CommonTablePage.Table>
                <CommonTablePage.Pagination {...paginationProps}></CommonTablePage.Pagination>
            </CommonTablePage>
        </Page>
    );
});

class Component extends React.Component {
    render() {
        return <Template {...this.props} />;
    }
}

export default Udesk.react.udeskify(Component);
